/**
 * Scouting API Service
 * Manages followed players and watched matches per user in Cosmos DB via backend API.
 * Mirrors the pattern of videoProgressApiService.js.
 *
 * API Base: {API_BASE_URL}/scouting
 * Auth: Bearer token via fetchWithAuth (MSAL)
 */

import fetchWithAuth from "../middlewares/fetchWithAuth";
import { API_BASE_URL } from "../config";

const SCOUTING_URL = `${API_BASE_URL}/scouting`;

/**
 * Authenticated fetch helper for scouting endpoints.
 * @param {string} url
 * @param {RequestInit} options
 * @param {Function} navigate
 * @returns {Promise<Response>}
 */
async function authFetch(url, options = {}, navigate) {
  const response = await fetchWithAuth(
    url,
    {
      ...options,
      headers: {
        "Content-Type": "application/json",
        ...options.headers,
      },
    },
    navigate
  );

  if (!response.ok) {
    throw new Error(`Scouting API error: ${response.status}`);
  }

  return response;
}

/**
 * Get all followed players for the authenticated user.
 * @param {Function} navigate
 * @returns {Promise<Array>}
 */
export async function getFollowedPlayers(navigate) {
  const response = await authFetch(
    `${SCOUTING_URL}/players`,
    { method: "GET" },
    navigate
  );
  const data = await response.json();
  return data || [];
}

/**
 * Follow a player (upsert).
 * @param {number|string} jugadorId
 * @param {Object} playerData - { jugadorNombre, equipoNombre, equipoId, tags }
 * @param {Function} navigate
 * @returns {Promise<Object>}
 */
export async function followPlayer(jugadorId, playerData, navigate) {
  if (jugadorId == null) return null;
  const response = await authFetch(
    `${SCOUTING_URL}/players/${encodeURIComponent(String(jugadorId))}`,
    {
      method: "PUT",
      body: JSON.stringify(playerData),
    },
    navigate
  );
  return response.json();
}

/**
 * Unfollow a player.
 * @param {number|string} jugadorId
 * @param {Function} navigate
 * @returns {Promise<void>}
 */
export async function unfollowPlayer(jugadorId, navigate) {
  if (jugadorId == null) return;
  await authFetch(
    `${SCOUTING_URL}/players/${encodeURIComponent(String(jugadorId))}`,
    { method: "DELETE" },
    navigate
  );
}

/**
 * Update notes and profile for a followed player.
 * @param {number|string} jugadorId
 * @param {Object} data - { notes, posiciones, pieDominante }
 * @param {Function} navigate
 * @returns {Promise<Object>}
 */
export async function updatePlayerNotes(jugadorId, data, navigate) {
  if (jugadorId == null) return null;
  const response = await authFetch(
    `${SCOUTING_URL}/players/${encodeURIComponent(String(jugadorId))}/notes`,
    {
      method: "PATCH",
      body: JSON.stringify(data),
    },
    navigate
  );
  return response.json();
}

/**
 * Get all matches marked to watch for the authenticated user.
 * @param {Function} navigate
 * @returns {Promise<Array>}
 */
export async function getWatchedMatches(navigate) {
  try {
    const response = await authFetch(
      `${SCOUTING_URL}/matches`,
      { method: "GET" },
      navigate
    );
    const data = await response.json();
    return data || [];
  } catch {
    return [];
  }
}

/**
 * Mark a match to watch (upsert).
 * @param {number|string} partidoId
 * @param {Object} matchData - { equipoLocal, equipoVisitante, equipoLocalId, equipoVisitanteId, fecha, jornada, competicion, reason, relatedPlayerIds }
 * @param {Function} navigate
 * @returns {Promise<Object>}
 */
export async function watchMatch(partidoId, matchData, navigate) {
  if (partidoId == null) return null;
  const response = await authFetch(
    `${SCOUTING_URL}/matches/${encodeURIComponent(String(partidoId))}`,
    {
      method: "PUT",
      body: JSON.stringify(matchData),
    },
    navigate
  );
  return response.json();
}

/**
 * Unmark a match (remove from watch list).
 * @param {number|string} partidoId
 * @param {Function} navigate
 * @returns {Promise<void>}
 */
export async function unwatchMatch(partidoId, navigate) {
  if (partidoId == null) return;
  await authFetch(
    `${SCOUTING_URL}/matches/${encodeURIComponent(String(partidoId))}`,
    { method: "DELETE" },
    navigate
  );
}

/**
 * Get upcoming matches from the authenticated user's watch agenda (fecha > now), sorted by date.
 * @param {Function} navigate
 * @returns {Promise<Array>}
 */
export async function getScoutingAgenda(navigate) {
  try {
    const response = await authFetch(
      `${SCOUTING_URL}/agenda`,
      { method: "GET" },
      navigate
    );
    const data = await response.json();
    return data || [];
  } catch {
    return [];
  }
}

/**
 * Get auto-discovered matches for followed players' teams (current season).
 * @param {Function} navigate
 * @returns {Promise<{upcoming: Array, recent: Array, players: Array}>}
 */
export async function getDiscoveredMatches(navigate) {
  try {
    const response = await authFetch(
      `${SCOUTING_URL}/discovered-matches`,
      { method: "GET" },
      navigate
    );
    const data = await response.json();
    return data || { upcoming: [], recent: [], players: [] };
  } catch {
    return { upcoming: [], recent: [], players: [] };
  }
}

// ─────────────────────────────────────────────
// MATCH NOTES (jugador + partido)
// ─────────────────────────────────────────────

/**
 * Get all match notes for the authenticated user.
 */
export async function getAllMatchNotes(navigate) {
  try {
    const response = await authFetch(
      `${SCOUTING_URL}/match-notes`,
      { method: "GET" },
      navigate
    );
    const data = await response.json();
    return data || [];
  } catch {
    return [];
  }
}

/**
 * Upsert a match note for a specific player+match.
 */
export async function upsertMatchNote(jugadorId, partidoId, noteData, navigate) {
  if (jugadorId == null || partidoId == null) return null;
  const response = await authFetch(
    `${SCOUTING_URL}/match-notes/${encodeURIComponent(String(jugadorId))}/${encodeURIComponent(String(partidoId))}`,
    {
      method: "PUT",
      body: JSON.stringify(noteData),
    },
    navigate
  );
  return response.json();
}

/**
 * Delete a match note for a specific player+match.
 */
export async function deleteMatchNote(jugadorId, partidoId, navigate) {
  if (jugadorId == null || partidoId == null) return;
  await authFetch(
    `${SCOUTING_URL}/match-notes/${encodeURIComponent(String(jugadorId))}/${encodeURIComponent(String(partidoId))}`,
    { method: "DELETE" },
    navigate
  );
}
